/**
 * MIN-241: pure ordering/navigation decisions for chat threads, shared by the
 * full-page sessions rail and the command-palette history list so they agree.
 *
 * ai-engine returns naive UTC datetimes (no trailing `Z`). Parsing them as
 * local time makes recent threads sort and render as future ("in 5 hours") —
 * append `Z` before parsing, but only when the string doesn't already carry a
 * zone marker.
 */

const HAS_ZONE = /[zZ]|[+-]\d{2}:?\d{2}$/;

export function parseChatThreadCreatedAt(createdAt: string | null): number {
  if (!createdAt) return 0;
  return Date.parse(HAS_ZONE.test(createdAt) ? createdAt : `${createdAt}Z`);
}

export function sortChatThreadsByCreatedAt<T extends { created_at: string | null }>(threads: T[]): T[] {
  return [...threads].sort((a, b) => parseChatThreadCreatedAt(b.created_at) - parseChatThreadCreatedAt(a.created_at));
}

/**
 * Where to land after deleting a thread. Leaves the clinician put unless the
 * thread they're currently reading is the one being deleted, in which case it
 * prefers the newer neighbor (the thread just above it in the newest-first
 * order), falling back to the older neighbor, and `null` when nothing remains.
 */
export function nextThreadAfterDelete<T>(orderedThreadIds: T[], deletedId: T, activeThreadId: T): T | null {
  if (deletedId !== activeThreadId) return activeThreadId;

  const index = orderedThreadIds.indexOf(deletedId);
  const remaining = orderedThreadIds.filter((id) => id !== deletedId);
  if (remaining.length === 0) return null;

  return orderedThreadIds[index - 1] ?? orderedThreadIds[index + 1] ?? remaining[0];
}
